<script setup lang="ts">
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { differenceInSeconds, formatDuration, intervalToDuration, isAfter, isBefore } from 'date-fns';
import { ref } from 'vue';

const useSession = useSessionStore();
const useUser = useUserStore();

const defaultValue = '5 Minutes';
const open = ref(false);
const countdown = ref(defaultValue);

async function resetTimeout() {
  useSession.extend();
}

watch(
  () => useSession.togglePopup,
  () => {
    const cookie = exportUserSession();
    if (useSession.togglePopup && open.value === false && cookie.value) {
      open.value = true;
    } else if (useSession.togglePopup === false && open.value === true) {
      open.value = false;
    }
  }
);

watch(
  () => useSession.timer,
  () => {
    const cookieSessionTimeout = parseInt(localStorage.getItem('sessionTimeout') ?? '0');

    let duration = intervalToDuration({
      start: new Date(),
      end: cookieSessionTimeout,
    });

    if (differenceInSeconds(cookieSessionTimeout, new Date()) > 300) {
      useSession.setTogglePopup(false);
    }

    if (isAfter(new Date(), cookieSessionTimeout)) {
      useSession.timeout();
    } else {
      countdown.value = formatDuration(duration, {
        delimiter: ', ',
      });
    }
  }
);

onMounted(async () => {
  if (useSession.timerActive) {
    useSession.startCountdownTimer();
  }
  if (useSession.togglePopup) {
    open.value = true;
  }
  const cookie = exportUserSession();
  if (cookie.value) {
    await useUser.getCurrentUser();
  }
});
</script>

<template>
  <div class="flex items-center justify-center">
    <TransitionRoot as="template" :show="open">
      <Dialog as="div" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="ease-in duration-200"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <!-- This element is to trick the browser into centering the modal contents. -->
          <span class="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              class="inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-center align-middle shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6 sm:align-middle"
            >
              <div>
                <div class="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" class="text-xl text-center font-medium leading-6 text-gray-900">
                    Do you need more time?
                  </DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">Your session will expire in</p>
                    <h4>{{ countdown }}</h4>
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-6 flex flex-col justify-center text-center">
                <button type="button" class="btn-primary w-full text-center block" @click="resetTimeout">
                  I need more time
                </button>
              </div>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>
